import React, { useState, useEffect, useRef } from "react";
import "./GunRegistrationModal.css";
import { getProducts } from "../../services/productService";
import { registerGun, updateGun } from "../../services/gunService";
import { QRCodeCanvas } from "qrcode.react";
import Popup from "../../components/ui/Popup";

const GunRegistrationModal = ({ isOpen, gun, onClose, onGunRegistered, onGunUpdated }) => {
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [status, setStatus] = useState("In Stock");
    const [qrCodeData, setQRCodeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const qrCodeRef = useRef(null);

    // Popup state
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState("success");

    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            fetchProducts().then(fetchedProducts => {
                // After products are loaded, handle edit mode
                if (gun) {
                    setIsEditing(true);
                    setSerialNumber(gun.serial_number || "");
                    setStatus(gun.status || "In Stock");

                    // Find the product ID using the product_name
                    if (gun.product_name && fetchedProducts.length > 0) {
                        const matchingProduct = fetchedProducts.find(
                            product => product.name === gun.product_name
                        );

                        if (matchingProduct) {
                            setSelectedProduct(String(matchingProduct.id));
                        } else {
                            setSelectedProduct("");
                            showPopup("Warning: Could not find exact product match", "warning");
                        }
                    } else if (gun.product_id) {
                        // If product_id exists, use it directly
                        setSelectedProduct(String(gun.product_id));
                    } else {
                        setSelectedProduct("");
                    }
                } else {
                    // Reset form for new registration
                    resetForm();
                }
                setLoading(false);
            });
        }
    }, [isOpen, gun]);

    const resetForm = () => {
        setIsEditing(false);
        setSelectedProduct("");
        setSerialNumber("");
        setStatus("In Stock");
        setQRCodeData(null);
        setPopupMessage("");
        setIsRegistered(false);
    };

    const fetchProducts = async () => {
        try {
            const data = await getProducts();
            setProducts(data);
            return data;
        } catch (error) {
            showPopup("Failed to load products. Please try again.", "error");
            return [];
        }
    };

    const showPopup = (message, type = "success") => {
        setPopupMessage(message);
        setPopupType(type);
    };

    const handlePopupClose = () => {
        setPopupMessage("");
    };

    // Create QR code data with comprehensive information
    const createQRCodeData = () => {
        const product = products.find(p => String(p.id) === String(selectedProduct));

        const qrData = {
            serialNumber: serialNumber,        // Changed to match expected property name
            registration_date: new Date().toISOString(),
            productId: selectedProduct        // Ensure product_id is included and properly named
        };

        // Add product details if available
        if (product) {
            qrData.product_name = product.name;
            qrData.brand = product.brand;
            qrData.country = product.country;
            qrData.category = product.category;

            // Add any other product details that might be available
            if (product.price) qrData.price = product.price;
            if (product.description) qrData.description = product.description;
            if (product.is_imported) qrData.is_imported = product.is_imported;
        }

        return JSON.stringify(qrData);
    };

    const handleSubmit = async () => {
        if (!selectedProduct || !serialNumber) {
            showPopup("Please fill in all fields", "error");
            return;
        }

        try {
            if (isEditing) {
                // Update existing gun
                await updateGun(gun.id, {
                    productId: selectedProduct,
                    serialNumber,
                    status
                });

                showPopup("Gun updated successfully!", "success");

                // Close modal after a short delay to allow user to see the success message
                setTimeout(() => {
                    if (onGunUpdated) {
                        onGunUpdated();
                        onClose();
                    }
                }, 1500);
            } else {
                // Don't allow re-registration if already registered
                if (isRegistered) {
                    showPopup("This gun has already been registered", "warning");
                    return;
                }

                // Register new gun
                const response = await registerGun({
                    productId: selectedProduct,
                    serialNumber,
                    status
                });

                // Create comprehensive QR code data
                const qrData = createQRCodeData();

                // Registration successful
                setQRCodeData(qrData);
                setIsRegistered(true); // Mark as registered to prevent duplicate submissions
                showPopup("Gun registered successfully!", "success");

                if (onGunRegistered) {
                    onGunRegistered();
                }
            }
        } catch (error) {
            // Check if error response contains a message
            const errorMessage = error.response?.data?.message ||
                (isEditing
                    ? "Failed to update gun. Please check your input."
                    : "Failed to register gun. Serial number may already exist.");

            showPopup(errorMessage, "error");
        }
    };

    // Updated printQRForThermal function with minimal design
    const printQRForThermal = (size = "small") => {
        // Create a new window for printing
        const printWindow = window.open('', '_blank');

        // Get size values based on selection
        let qrSize, paperWidth, paperHeight;
        switch(size) {
            case "tiny":
                qrSize = 90;
                paperWidth = 58;
                paperHeight = 40;
                break;
            case "small":
                qrSize = 110;
                paperWidth = 80;
                paperHeight = 45;
                break;
            case "medium":
                qrSize = 140;
                paperWidth = 90;
                paperHeight = 60;
                break;
            default:
                qrSize = 110;
                paperWidth = 80;
                paperHeight = 45;
        }

        // Get QR code data URL directly
        const qrCanvas = qrCodeRef.current;
        const qrDataURL = qrCanvas ? qrCanvas.toDataURL("image/png") : "";

        // Write the print content
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>QR Code</title>
            <style>
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                
                body {
                    width: ${paperWidth}mm;
                    height: ${paperHeight}mm;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background: #fff;
                }
                
                .qr-image {
                    width: ${qrSize}px;
                    height: ${qrSize}px;
                }
                
                @page {
                    size: ${paperWidth}mm ${paperHeight}mm;
                    margin: 0;
                }
                
                @media print {
                    html, body {
                        width: ${paperWidth}mm;
                        height: ${paperHeight}mm;
                    }
                }
            </style>
        </head>
        <body>
            <img class="qr-image" src="${qrDataURL}" alt="QR Code" />
            <script>
                // Print and close automatically
                window.onload = function() {
                    setTimeout(() => {
                    if (onGunUpdated) {
                        onGunUpdated();
                    }
                    if (refreshGunList) {
                        refreshGunList(); // Call refresh gun list directly
                    }
                    onClose();
                }, 1500);
                };
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
    };

    // Find the product name for the title
    const getProductName = () => {
        if (!isEditing) return "";

        // If we already have it in the gun object
        if (gun?.product_name) return gun.product_name;

        // Otherwise try to find it from the selected product
        if (selectedProduct && products.length > 0) {
            const product = products.find(p => String(p.id) === String(selectedProduct));
            return product ? product.name : "";
        }

        return "";
    };

    return (
        isOpen && (
            <div className="gunreg-modal-overlay">
                <div className={`gunreg-modal-content ${qrCodeData ? 'gunreg-with-qr' : ''}`}>
                    <h2 className="gunreg-title">
                        {isEditing
                            ? `Edit Gun: ${getProductName()} (${gun?.serial_number || ""})`
                            : "Register New Gun"
                        }
                    </h2>

                    {loading ? (
                        <div className="gunreg-loading">Loading...</div>
                    ) : (
                        <>
                            <div className={`gunreg-form-section ${qrCodeData ? 'gunreg-form-compact' : ''}`}>
                                <div className="gunreg-form-group">
                                    <label className="gunreg-label">Select Product:</label>
                                    <select
                                        className="gunreg-select"
                                        value={selectedProduct}
                                        onChange={(e) => setSelectedProduct(e.target.value)}
                                        disabled={qrCodeData !== null}
                                    >
                                        <option value="">-- Select Gun Model --</option>
                                        {products.map((product) => (
                                            <option key={product.id} value={String(product.id)}>
                                                {product.name} ({product.brand} - {product.country})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="gunreg-form-group">
                                    <label className="gunreg-label">Serial Number:</label>
                                    <input
                                        className="gunreg-input"
                                        type="text"
                                        placeholder="Enter Serial Number"
                                        value={serialNumber}
                                        onChange={(e) => setSerialNumber(e.target.value)}
                                        disabled={qrCodeData !== null}
                                    />
                                </div>

                                <div className="gunreg-form-group">
                                    <label className="gunreg-label">Status:</label>
                                    <select
                                        className="gunreg-select"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        disabled={qrCodeData !== null}
                                    >
                                        <option value="In Stock">In Stock</option>
                                        <option value="Sold">Sold</option>
                                        <option value="Under Repair">Under Repair</option>
                                    </select>
                                </div>
                            </div>

                            {qrCodeData && (
                                <div className="gunreg-qr-section">
                                    <h3 className="gunreg-qr-title">QR Code</h3>
                                    <div className="gunreg-qr-code">
                                        <QRCodeCanvas
                                            ref={qrCodeRef}
                                            value={qrCodeData}
                                            size={120}
                                            level="H" // High error correction for better scanning
                                            includeMargin={true}
                                        />
                                    </div>
                                    <div className="gunreg-qr-info">
                                        <p className="gunreg-qr-description">
                                            This QR code contains complete gun details including serial number,
                                            product information, and registration date.
                                        </p>
                                    </div>
                                    <div className="gunreg-print-options">
                                        <h4 className="gunreg-print-title">Print Label Size:</h4>
                                        <div className="gunreg-print-buttons">
                                            <button
                                                className="gunreg-print-btn"
                                                onClick={() => printQRForThermal('tiny')}
                                            >
                                                Small (58mm)
                                            </button>
                                            <button
                                                className="gunreg-print-btn"
                                                onClick={() => printQRForThermal('small')}
                                            >
                                                Standard (80mm)
                                            </button>
                                            <button
                                                className="gunreg-print-btn"
                                                onClick={() => printQRForThermal('medium')}
                                            >
                                                Large
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="gunreg-modal-actions">
                                <button
                                    className="gunreg-cancel-btn"
                                    onClick={onClose}
                                >
                                    {qrCodeData ? 'Done' : 'Cancel'}
                                </button>

                                {!qrCodeData && (
                                    <button
                                        className="gunreg-register-btn"
                                        onClick={handleSubmit}
                                    >
                                        {isEditing ? "Update" : "Register"}
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {/* Popup for success/error messages */}
                    {popupMessage && (
                        <Popup
                            message={popupMessage}
                            type={popupType}
                            onClose={handlePopupClose}
                            autoClose={true}
                            duration={3000}
                        />
                    )}
                </div>
            </div>
        )
    );
};

export default GunRegistrationModal;